/*
 * Masculino Latino: global behaviour.
 * Cart (Ajax API + Section Rendering), cart drawer, delivery date, in-view reveals,
 * sticky header, card quick add, reels autoplay and count-up.
 */
(function () {
  document.documentElement.classList.add('ml-js');
  const S = window.mlSettings || {};
  const T = window.mlStrings || {};
  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('ml-motion-off');

  /* ---------------- Cart ---------------- */
  const DRAWER_SECTION = 'ml-cart-drawer';

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || T.cartError || 'Error');
      err.data = data;
      throw err;
    }
    return data;
  }

  function renderDrawer(sections) {
    const html = sections && sections[DRAWER_SECTION];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-ml-drawer-inner]');
    const current = document.querySelector('[data-ml-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
    const count = fresh ? fresh.getAttribute('data-item-count') : null;
    if (count !== null) updateCount(Number(count));
  }

  function updateCount(count) {
    document.querySelectorAll('[data-ml-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      el.classList.remove('ml-pop');
      void el.offsetWidth;
      el.classList.add('ml-pop');
    });
    document.querySelectorAll('[data-ml-cart-label]').forEach((el) => {
      const tpl = el.getAttribute('data-ml-cart-label');
      el.setAttribute('aria-label', tpl.replace('[count]', count));
    });
  }

  const MLCart = {
    /** items: [{ id, quantity, properties? }] */
    async add(items, source) {
      const data = await postJSON(S.cartAddUrl + '.js', { items, sections: DRAWER_SECTION, sections_url: window.location.pathname });
      renderDrawer(data.sections);
      document.dispatchEvent(new CustomEvent('ml:cart:updated', { detail: { action: 'add', items, source } }));
      if (source !== 'offer-box') MLCart.open();
      return data;
    },
    async change(line, quantity) {
      const data = await postJSON(S.cartChangeUrl + '.js', { line, quantity, sections: DRAWER_SECTION, sections_url: window.location.pathname });
      renderDrawer(data.sections);
      document.dispatchEvent(new CustomEvent('ml:cart:updated', { detail: { action: 'change', line, quantity, cart: data } }));
      return data;
    },
    open() {
      const d = document.querySelector('ml-cart-drawer');
      if (d) d.open();
      else window.location.href = S.cartUrl;
    },
  };
  window.MLCart = MLCart;

  class MLCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-ml-drawer-panel]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ml-drawer-close]')) {
          e.preventDefault();
          this.close();
          return;
        }
        const qty = e.target.closest('[data-ml-qty]');
        if (qty) {
          e.preventDefault();
          this.setLoading(true);
          MLCart.change(Number(qty.dataset.line), Number(qty.dataset.qty))
            .catch((err) => this.showError(err.message))
            .finally(() => this.setLoading(false));
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-ml-cart-open]');
        if (trigger) {
          e.preventDefault();
          this.open(trigger);
        }
      });
    }
    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.setAttribute('open', '');
      document.documentElement.classList.add('ml-lock');
      requestAnimationFrame(() => {
        const close = this.querySelector('[data-ml-drawer-close].ml-icon-btn');
        if (close) close.focus();
      });
    }
    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('ml-lock');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
    setLoading(on) {
      this.toggleAttribute('loading', on);
    }
    showError(msg) {
      const el = this.querySelector('[data-ml-drawer-error]');
      if (el) {
        el.textContent = msg || T.cartError;
        el.hidden = false;
      }
    }
    trapFocus(e) {
      const f = Array.from(this.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')).filter(
        (el) => el.offsetParent !== null
      );
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  if (!customElements.get('ml-cart-drawer')) customElements.define('ml-cart-drawer', MLCartDrawer);

  /* ---------------- Delivery date (IST) ---------------- */
  function deliveryDate() {
    const d = S.delivery || {};
    const now = new Date();
    const ist = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + 330 * 60000);
    const day = new Date(ist.getFullYear(), ist.getMonth(), ist.getDate());
    const skipSun = d.skipSundays !== false;
    const addDays = (date, n) => {
      let left = n;
      while (left > 0) {
        date.setDate(date.getDate() + 1);
        if (skipSun && date.getDay() === 0) continue;
        left -= 1;
      }
    };
    if (ist.getHours() >= Number(d.cutoffHourIst == null ? 14 : d.cutoffHourIst)) addDays(day, 1);
    addDays(day, Number(d.processingDays || 0) + Number(d.transitDaysMax || 0));
    const days = (T.days || 'Sun,Mon,Tue,Wed,Thu,Fri,Sat').split(',');
    const months = (T.months || 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',');
    return days[day.getDay()] + ', ' + day.getDate() + ' ' + months[day.getMonth()];
  }
  function fillDelivery(root) {
    const date = deliveryDate();
    (root || document).querySelectorAll('[data-ml-delivery]').forEach((el) => {
      el.innerHTML = (T.deliveredBy || 'Order now, delivered by <strong>[date]</strong>').replace('[date]', date);
    });
  }
  window.MLDelivery = { date: deliveryDate, fill: fillDelivery };

  /* ---------------- In view ---------------- */
  let io;
  function observeInView(root) {
    const els = (root || document).querySelectorAll('.ml-reveal, [data-ml-inview]');
    if (!('IntersectionObserver' in window) || reduceMotion() || (window.Shopify && window.Shopify.designMode)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    io =
      io ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            if (entry.target.hasAttribute('data-ml-count')) countUp(entry.target);
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
      );
    els.forEach((el) => {
      if (!el.classList.contains('is-in')) io.observe(el);
    });
  }

  function countUp(el) {
    const target = Number(el.getAttribute('data-ml-count')) || 0;
    if (reduceMotion()) {
      el.textContent = target.toLocaleString('en-IN');
      return;
    }
    const start = performance.now();
    const dur = 2000;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * eased).toLocaleString('en-IN');
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------------- Sticky header: hide on scroll down ---------------- */
  function stickyHeader() {
    const header = document.querySelector('[data-ml-sticky-header]');
    if (!header) return;
    let last = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        const open = header.querySelector('details[open]');
        if (!open) header.classList.toggle('is-hidden', y > last && y > 160);
        last = y;
      },
      { passive: true }
    );
  }

  /* ---------------- Card quick add ---------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ml-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-soldout')) return;
    btn.classList.add('is-selected');
    MLCart.add([{ id: Number(btn.dataset.variantId), quantity: 1 }], 'card')
      .catch((err) => console.warn(err.message))
      .finally(() => setTimeout(() => btn.classList.remove('is-selected'), 900));
  });

  /* ---------------- Reels ---------------- */
  function reels(root) {
    const vids = (root || document).querySelectorAll('[data-ml-reel] video');
    if (!vids.length) return;
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const vio = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) en.target.play().catch(() => {});
          else en.target.pause();
        });
      },
      { threshold: 0.5 }
    );
    vids.forEach((v) => vio.observe(v));
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ml-sound]');
    if (!b) return;
    const v = b.closest('[data-ml-reel]')?.querySelector('video');
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    b.setAttribute('aria-pressed', String(!v.muted));
  });

  /* ---------------- Dropdowns (header menu and Shop panel) ---------------- */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-ml-dropdown][open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-ml-dropdown][open]').forEach((d) => {
      d.removeAttribute('open');
      d.querySelector('summary')?.focus();
    });
  });
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.matches('.ml-header__drawer')) return;
      document.documentElement.classList.toggle('ml-lock', d.open);
    },
    true
  );

  function init(root) {
    fillDelivery(root);
    observeInView(root);
    reels(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    stickyHeader();
  });
  document.addEventListener('ml:cart:updated', () => fillDelivery(document.querySelector('ml-cart-drawer')));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
